/**
 * @fileoverview Hybrid WYSIWYG document builder canvas (Phase 2, Workstreams A/C/D/E4/H1/I).
 * @module ui-admin/components/DocumentCanvasBuilder
 *
 * Responsibilities: - Render a DocumentLayout as a full-width paged band canvas (what's shown prints).
 *                   - Author both the document Body and an optional Cover Page through one
 *                     parameterised canvas, switched by an in-canvas Body / Cover Page tab (H1).
 *                   - Add components from a collapsible left palette drawer; edit the selected band
 *                     in a right properties panel (Workstream I). By default the panel uses
 *                     position:fixed to overlay the live-preview pane; when the host supplies
 *                     `propertiesPortalTarget`, the panel is portaled into that container instead so
 *                     the host can present Properties as a tab beside the preview (full pane width).
 *                   - Mark dynamic bands with an affordance ("repeats per item; expands with data")
 *                     and a sample-data preview toggle that renders real expansion (D2).
 *                   - Configure per-slot letterhead/cover logos (department vs uploaded) (E4).
 * Dependencies:     - @firesquirrel/document-render (palette + band ops + section renderers + model
 *                     + logo resolver), @dnd-kit (drag-reorder), dompurify (preview sanitization),
 *                     lucide-react (chrome icons), AccordionSection/SlideOverPanel (shell drawers).
 * Architecture:     - Controlled component (value + onChange) shared by tenant-web and system-web.
 *                     Rich-text editing is injected via `renderRichTextEditor`; logo upload is
 *                     injected via `onPickLogo` so ui-admin stays editor/upload-agnostic. Band ops
 *                     are the pure, validated operations from document-render; no API/Dexie calls.
 *
 * @component DocumentCanvasBuilder
 * Purpose:     Drag-and-drop document builder over the canonical DocumentLayout model.
 * Props:       DocumentCanvasBuilderProps.
 * Behavior:    Pure band operations from @firesquirrel/document-render; no API calls.
 * Data Source: none (controlled). Sample preview uses caller-provided sampleData.
 * Constraints: presentation only; emits sanitized HTML previews.
 * Performance: section previews memoized per band.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DOMPurify from 'dompurify';
import {
  GripVertical, MoreVertical, Plus, Trash2, Copy, ArrowUp, ArrowDown,
  FileText, Layers, PanelLeftOpen, ImageIcon, HelpCircle, Compass, X,
} from 'lucide-react';
import { BuilderHelpPanel } from '../BuilderHelpPanel/BuilderHelpPanel';
import { GuidedTour } from '../GuidedTour/GuidedTour';
import { ComponentHelpPopover } from '../ComponentHelpPopover/ComponentHelpPopover';
import { BUILDER_TOUR_STEPS, TOUR_TARGET } from './builderTourSteps';
import type { HelpSectionId } from '../../help/types';
import {
  paletteItemsForSurface, createBand, addBand, moveBand, removeBand, duplicateBand,
  paletteItemFor, isDynamicBand, dynamicBandAffordance, defaultLogoDescriptor,
  renderViolations, renderPhotos, renderMetadata, renderFormResponses, renderAdvancedFields,
  type DocumentLayout, type Band, type BandKind, type PaletteGroup,
  type DocumentRenderData, type BorderStyle, type LogoDescriptor,
  type ElementStyle, type Element as DocElement,
} from '@firesquirrel/document-render';
import { SlideOverPanel } from '../SlideOverPanel/SlideOverPanel';
import ConfirmActionModal from '../ui/ConfirmActionModal';
import { DocumentSectionEditor } from '../template-builder/documentSectionEditors/DocumentSectionEditor';
import { ElementStyleControls } from '../template-builder/documentSectionEditors/ElementStyleControls';
import { SECTION_ICONS } from '../template-builder/documentSectionEditors/types';
import type {
  DocumentConfig, SectionConfig, SectionId,
} from '../template-builder/documentSectionEditors/types';

/**
 * Maps a canvas band kind to the legacy `documentConfig` section whose editor exposes that band's
 * full attribute set (dept info, custom title/subtitle, per-section options). Selecting a band on
 * the canvas renders the same shared section editor the Section list uses, so the two surfaces
 * expose identical options and write to one model (Section↔Canvas parity).
 *
 * Only **singleton** band kinds (`allowMultiple: false` in the palette) are mapped: their config
 * slice is 1:1 with the band, so editing the band edits its own config. Repeatable content kinds
 * (richText, callout, signatureBlock, image, …) are intentionally excluded — each is its
 * own per-band content authored by the canvas's own editor, and mapping them to a singleton config
 * slice would make every instance edit the same shared config.
 */
const BAND_KIND_SECTION: Partial<Record<BandKind, SectionId>> = {
  letterhead: 'header',
  coverPage: 'cover',
  recipientBlock: 'recipientBlock',
  subjectLine: 'subjectLine',
  salutation: 'salutation',
  metadataGrid: 'metadata',
  violationsList: 'violations',
  photoGallery: 'photos',
  legal: 'legal',
  footer: 'footer',
};

const SECTION_ID_SET = new Set<string>(Object.values(BAND_KIND_SECTION));

/**
 * Resolve the section a selected band edits. Config-derived bands carry the section id as the band
 * id (e.g. 'header', 'violations'); palette-added bands use a generated id, so fall back to the
 * band-kind map. Returns null for bands without a shared section editor (image/divider/spacer/…).
 */
function sectionIdForBand(band: Band): SectionId | null {
  if (SECTION_ID_SET.has(band.id)) return band.id as SectionId;
  return BAND_KIND_SECTION[band.kind] ?? null;
}

/** Find the SectionConfig for a section id, or synthesize a minimal one for the editor. */
function sectionConfigFor(config: DocumentConfig, sectionId: SectionId): SectionConfig {
  const found = config.sections.find((s) => s.id === sectionId);
  if (found) return found;
  return { id: sectionId, label: sectionId, icon: SECTION_ICONS[sectionId], description: '', enabled: true };
}

/** localStorage key recording that the first-visit tour prompt has been seen/dismissed. */
const TOUR_SEEN_KEY = 'doc-builder-tour-seen';

/** Read whether the first-visit tour prompt has already been seen (SSR/private-mode safe). */
function readTourSeen(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(TOUR_SEEN_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Persist that the first-visit tour prompt has been seen. */
function markTourSeen(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TOUR_SEEN_KEY, 'true');
  } catch {
    /* ignore persistence failures */
  }
}

export interface DocumentCanvasBuilderProps {
  layout: DocumentLayout;
  onChange: (layout: DocumentLayout) => void;
  /** Resolved sample data used to preview dynamic-band expansion (D2). */
  sampleData?: DocumentRenderData;
  /** App-supplied rich-text editor for richText bands (keeps ui-admin editor-agnostic). */
  renderRichTextEditor?: (props: { html: string; onChange: (html: string) => void }) => React.ReactNode;
  /**
   * Host-supplied logo upload (E4). Opens the app's uploadedFileService picker/upload flow and
   * resolves to the stored UploadedFile id (or null if cancelled). Keeps ui-admin upload-agnostic.
   */
  onPickLogo?: () => Promise<string | null>;
  /** Resolve an uploaded logo file id to a thumbnail URL for the picker preview (E4). */
  resolveLogoThumb?: (uploadedFileId: string) => string | undefined;
  /** Resolve the current department primary logo thumbnail for department-sourced logo slots. */
  resolveDepartmentLogoThumb?: () => string | undefined;
  /** Resolve the current department secondary logo thumbnail for department-sourced logo slots. */
  resolveDepartmentSecondLogoThumb?: () => string | undefined;
  /** Optional helper text shown when the department source represents preview branding. */
  departmentLogoNote?: string;
  /**
   * The legacy `documentConfig` the renderer reads for section content (dept info, custom
   * title/subtitle, per-section options). When supplied with `onConfigChange`, selecting a band
   * renders the same shared section editor the Section list uses — merging the Sections options
   * into the canvas property panel so both surfaces expose identical attributes (Section↔Canvas
   * parity). Omit to keep the canvas-only (layout-only) property panel.
   */
  config?: DocumentConfig;
  /** Patch the legacy `documentConfig` from the merged section editor (controlled). */
  onConfigChange?: (config: DocumentConfig) => void;
  /**
   * Controlled selection (single-canvas mode). When provided, the selected band id is owned by the
   * host (so an external activation list and the canvas share one selection). Omit for the legacy
   * self-managed selection.
   */
  selectedId?: string | null;
  /** Notifies the host when the selected band changes (pairs with `selectedId`). */
  onSelectedIdChange?: (id: string | null) => void;
  /**
   * Single-canvas activation mode: render only enabled bands on the canvas (disabled bands are
   * hidden, owned by the external activation list). Defaults to false — the legacy canvas renders
   * disabled bands dimmed with an inline enable toggle.
   */
  activationDriven?: boolean;
  /**
   * Optional host container into which the band properties panel is portaled instead of being
   * rendered as a fixed right-side overlay. When supplied, the panel fills the host (so the host
   * can present it as a tab alongside the live preview, giving rich-text editing the full pane
   * width). Omit to keep the default `position:fixed` slide-over overlay.
   */
  propertiesPortalTarget?: HTMLElement | null;
}

const GROUP_LABEL: Record<PaletteGroup, string> = {
  structural: 'Structural',
  content: 'Content',
  dynamic: 'Dynamic data',
};

type BuilderSurface = 'body' | 'coverPage';
type BandSlot = 'header' | 'body' | 'footer' | 'cover';

interface BandSelection {
  band: Band;
  slot: BandSlot;
  index: number;
}

/** Bands belonging to the active surface (body incl. fixed header/footer, or cover page). */
function getSelectableBands(layout: DocumentLayout, surface: BuilderSurface): BandSelection[] {
  if (surface === 'coverPage') {
    return (layout.coverLetter?.bands ?? []).map((band, index) => ({ band, slot: 'cover' as const, index }));
  }
  return [
    ...(layout.header ? [{ band: layout.header, slot: 'header' as const, index: 0 }] : []),
    ...layout.body.map((band, index) => ({ band, slot: 'body' as const, index })),
    ...(layout.footer ? [{ band: layout.footer, slot: 'footer' as const, index: 0 }] : []),
  ];
}

/** Immutably update a single band by id across header/body/footer/cover. */
function updateBand(layout: DocumentLayout, id: string, patch: Partial<Band>): DocumentLayout {
  if (layout.header?.id === id) return { ...layout, header: { ...layout.header, ...patch } };
  if (layout.footer?.id === id) return { ...layout, footer: { ...layout.footer, ...patch } };
  if (layout.coverLetter?.bands.some((b) => b.id === id)) {
    return {
      ...layout,
      coverLetter: { ...layout.coverLetter, bands: layout.coverLetter.bands.map((b) => (b.id === id ? { ...b, ...patch } : b)) },
    };
  }
  return { ...layout, body: layout.body.map((b) => (b.id === id ? { ...b, ...patch } : b)) };
}

/** Band kinds whose primary text element supports per-element typography (Phase E styling). */
const TEXT_STYLEABLE_KINDS: Set<BandKind> = new Set(['richText', 'callout']);

/**
 * Set (or clear) the ElementStyle on a band's primary text element, immutably.
 * Targets the first `text` element; if none exists, creates one so the style is retained.
 */
function setPrimaryTextStyle(elements: DocElement[], style: ElementStyle | undefined): DocElement[] {
  const idx = elements.findIndex((el) => el.type === 'text');
  if (idx === -1) {
    return [...elements, { id: `el-${Date.now()}`, type: 'text', style, html: '' }];
  }
  return elements.map((el, i) => (i === idx ? { ...el, style } : el));
}

/** Immutably remove a band by id across header/body/footer/cover. */
function removeSelectableBand(layout: DocumentLayout, id: string): DocumentLayout {
  if (layout.header?.id === id) return { ...layout, header: null };
  if (layout.footer?.id === id) return { ...layout, footer: null };
  if (layout.coverLetter?.bands.some((b) => b.id === id)) {
    return { ...layout, coverLetter: { ...layout.coverLetter, bands: layout.coverLetter.bands.filter((b) => b.id !== id) } };
  }
  return removeBand(layout, id);
}

function slotLabel(slot: BandSlot): string {
  if (slot === 'header') return 'Page header';
  if (slot === 'footer') return 'Page footer';
  if (slot === 'cover') return 'Cover page';
  return 'Body';
}

/**
 * Apply a body-oriented band op to whichever surface is active. The pure ops operate on
 * `layout.body`; for the cover page we route through a pseudo-layout whose body is the cover
 * bands, then write the result back to `coverLetter.bands` (reuses the validated singleton logic).
 */
function applySurfaceOp(
  layout: DocumentLayout,
  surface: BuilderSurface,
  op: (l: DocumentLayout) => DocumentLayout,
): DocumentLayout {
  if (surface === 'body') return op(layout);
  const pseudo: DocumentLayout = { ...layout, header: null, footer: null, body: layout.coverLetter?.bands ?? [] };
  const next = op(pseudo);
  return { ...layout, coverLetter: { ...(layout.coverLetter ?? { bands: [] }), bands: next.body } };
}

/** Render a dynamic band's sample HTML from the resolved sample data (guarded). */
function renderDynamicSample(kind: BandKind, data: DocumentRenderData): string {
  try {
    switch (kind) {
      case 'violationsList': return renderViolations(data);
      case 'photoGallery': return renderPhotos(data.photos ?? [], data.documentConfig);
      case 'metadataGrid': return renderMetadata(data.metadataFields ?? []);
      case 'formResponses': return renderFormResponses(data.formResponses ?? []);
      case 'advancedFields': return renderAdvancedFields(data.advancedFields ?? []);
      default: return '';
    }
  } catch {
    return '';
  }
}

/** Dynamic-band affordance + sample-data preview (shared by sortable and fixed band cards). */
function DynamicAffordance({
  band, slot, sampleData, showSample, onToggleSample,
}: {
  band: Band; slot: BandSlot; sampleData?: DocumentRenderData; showSample: boolean; onToggleSample: () => void;
}) {
  const affordance = dynamicBandAffordance(band.kind);
  const suffix = slot === 'body' || slot === 'cover' ? '' : `-${slot}`;
  const samplePreview = useMemo(
    () => (showSample && sampleData ? DOMPurify.sanitize(renderDynamicSample(band.kind, sampleData)) : ''),
    [showSample, sampleData, band.kind],
  );
  if (!affordance) return null;
  return (
    <div className="mt-2 rounded-md border border-dashed border-violet-300 bg-violet-50 p-2 text-xs text-violet-800">
      <div>{affordance}</div>
      <button
        type="button"
        data-testid={`sample-toggle${suffix}`}
        onClick={(e) => { e.stopPropagation(); onToggleSample(); }}
        className="mt-1 cursor-pointer border-none bg-transparent text-[11px] text-violet-700 underline"
      >
        {showSample ? 'Hide sample preview' : 'Preview with sample data'}
      </button>
      {showSample && (
        sampleData
          ? <div data-testid={`sample-preview${suffix}`} className="mt-1.5 border border-gray-200 bg-white p-1.5" dangerouslySetInnerHTML={{ __html: samplePreview }} />
          : <div data-testid={`sample-preview${suffix}`} className="mt-1.5 italic text-gray-400">No sample data available.</div>
      )}
    </div>
  );
}

/** Overflow (⋮) menu of band actions; keeps the band card chrome clean (Workstream I). */
function BandOverflowMenu({
  index, total, canDuplicate, onDuplicate, onDelete, onMove, tourId,
}: {
  index: number; total: number; canDuplicate: boolean;
  onDuplicate: () => void; onDelete: () => void; onMove: (dir: -1 | 1) => void;
  tourId?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Band actions"
        aria-expanded={open}
        data-tour={tourId}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-10 mt-1 w-40 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" role="menuitem" aria-label="Move up" disabled={index === 0}
            onClick={() => { onMove(-1); setOpen(false); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:text-gray-300">
            <ArrowUp className="h-3.5 w-3.5" /> Move up
          </button>
          <button type="button" role="menuitem" aria-label="Move down" disabled={index === total - 1}
            onClick={() => { onMove(1); setOpen(false); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:text-gray-300">
            <ArrowDown className="h-3.5 w-3.5" /> Move down
          </button>
          {canDuplicate && (
            <button type="button" role="menuitem" aria-label="Duplicate band"
              onClick={() => { onDuplicate(); setOpen(false); }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50">
              <Copy className="h-3.5 w-3.5" /> Duplicate
            </button>
          )}
          <button type="button" role="menuitem" aria-label="Delete band"
            onClick={() => { onDelete(); setOpen(false); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * @component InsertHere
 * Purpose:     Thin "+" affordance rendered between band cards (and after the last) so authors can
 *              insert a component — rich text, page break, divider, etc. — at any position, not only
 *              by appending then dragging. Makes the "Add component" palette discoverable in-context.
 * Props:       index (visible-list position to insert before), onInsert(index), trailing (after-last).
 * Behavior:    Subtle by default; reveals a labelled "Insert here" pill on hover/focus.
 * Data Source: none (presentation-only; parent owns the layout mutation).
 * Constraints: presentation-only — no direct API calls, no Dexie access.
 */
function InsertHere({ index, onInsert, trailing }: { index: number; onInsert: (index: number) => void; trailing?: boolean }) {
  return (
    <div className={`group/insert relative flex items-center justify-center ${trailing ? 'py-1.5' : 'py-0.5'}`}>
      <button
        type="button"
        onClick={() => onInsert(index)}
        data-testid={`insert-here-${index}`}
        aria-label="Insert component here"
        title="Insert a component here"
        className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-400 opacity-0 transition hover:border-orange-400 hover:text-orange-600 focus:opacity-100 group-hover/insert:opacity-100"
      >
        <Plus className="h-3 w-3" /> Insert here
      </button>
    </div>
  );
}

/** A draggable body/cover band card with clean chrome (drag | label+badges | toggle | ⋮). */
function BandCard({
  band, index, total, selected, sampleData, showSample,
  onSelect, onDuplicate, onDelete, onMove, onToggleSample, onToggleEnabled,
  helpOpen, onToggleHelp, onCloseHelp, onOpenFullHelp, tourDynamic, tourControls,
}: {
  band: Band; index: number; total: number; selected: boolean;
  sampleData?: DocumentRenderData; showSample: boolean;
  onSelect: () => void; onDuplicate: () => void; onDelete: () => void;
  onMove: (dir: -1 | 1) => void; onToggleSample: () => void; onToggleEnabled: () => void;
  helpOpen: boolean; onToggleHelp: () => void; onCloseHelp: () => void;
  onOpenFullHelp: (section: HelpSectionId) => void;
  tourDynamic?: boolean; tourControls?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: band.id });
  const item = paletteItemFor(band.kind);
  const dynamic = isDynamicBand(band.kind);
  const disabled = band.enabled === false;
  const helpBtnRef = useRef<HTMLButtonElement>(null);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-band-kind={band.kind}
      data-testid={`band-${band.kind}`}
      data-tour={tourDynamic ? TOUR_TARGET.dynamicBand : undefined}
      onClick={onSelect}
      className={`mb-2 rounded-lg border bg-white p-3 transition-shadow ${selected ? 'border-orange-500 ring-2 ring-orange-300' : 'border-gray-300 hover:border-gray-400'} ${disabled ? 'bg-gray-50' : ''}`}
    >
      <div className="flex items-center gap-2">
        <button type="button" aria-label="Drag to reorder" {...attributes} {...listeners}
          className="cursor-grab border-none bg-transparent text-gray-400 hover:text-gray-600">
          <GripVertical className="h-4 w-4" />
        </button>
        <span className={`font-semibold ${disabled ? 'text-gray-400' : 'text-gray-800'}`}>{item?.label ?? band.kind}</span>
        <span className="text-[10px] uppercase tracking-wide text-gray-400">{item ? GROUP_LABEL[item.group] : ''}</span>
        {dynamic && (
          <span data-testid="dynamic-badge" className="rounded-sm border border-violet-200 px-1.5 py-px text-[10px] text-violet-700">
            Dynamic
          </span>
        )}
        <span className="flex-1" />
        <label className="flex items-center gap-1 text-[11px] text-gray-500" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" aria-label="Band enabled" checked={!disabled} onChange={onToggleEnabled} />
        </label>
        <BandOverflowMenu
          index={index}
          total={total}
          canDuplicate={item?.allowMultiple !== false}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onMove={onMove}
          tourId={tourControls ? TOUR_TARGET.bandMenu : undefined}
        />
        <button
          ref={helpBtnRef}
          type="button"
          aria-label={`Help for ${item?.label ?? band.kind}`}
          aria-expanded={helpOpen}
          data-testid={`band-help-${band.kind}`}
          data-tour={tourControls ? TOUR_TARGET.bandHelp : undefined}
          onClick={(e) => { e.stopPropagation(); onToggleHelp(); }}
          className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
        <ComponentHelpPopover
          kind={band.kind}
          anchorRef={helpBtnRef}
          open={helpOpen}
          onClose={onCloseHelp}
          onOpenFullHelp={onOpenFullHelp}
        />
      </div>
      {dynamic && (
        <DynamicAffordance band={band} slot="body" sampleData={sampleData} showSample={showSample} onToggleSample={onToggleSample} />
      )}
    </div>
  );
}

/** A non-draggable fixed (page header / footer) band card. */
function FixedBandCard({
  band, slot, selected, sampleData, showSample,
  onSelect, onDelete, onToggleSample,
}: {
  band: Band; slot: Exclude<BandSlot, 'body' | 'cover'>; selected: boolean;
  sampleData?: DocumentRenderData; showSample: boolean;
  onSelect: () => void; onDelete: () => void; onToggleSample: () => void;
}) {
  const item = paletteItemFor(band.kind);
  const dynamic = isDynamicBand(band.kind);
  return (
    <div
      data-band-kind={band.kind}
      data-testid={`fixed-band-${slot}`}
      onClick={onSelect}
      className={`mb-2 rounded-lg border bg-white p-3 ${selected ? 'border-orange-500 ring-2 ring-orange-300' : 'border-gray-300 hover:border-gray-400'} ${band.enabled === false ? 'bg-gray-50' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{slotLabel(slot)}</span>
        <span className="font-semibold text-gray-800">{item?.label ?? band.kind}</span>
        {dynamic && (
          <span data-testid="dynamic-badge" className="rounded-sm border border-violet-200 px-1.5 py-px text-[10px] text-violet-700">Dynamic</span>
        )}
        <span className="flex-1" />
        <button type="button" aria-label={`Delete ${slotLabel(slot).toLowerCase()} band`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="rounded-sm p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {dynamic && (
        <DynamicAffordance band={band} slot={slot} sampleData={sampleData} showSample={showSample} onToggleSample={onToggleSample} />
      )}
    </div>
  );
}

/** A single letterhead/cover logo slot with department/uploaded source picker (E4).
 *  When source is 'department', a sub-selector lets the user pick the department's
 *  primary or secondary logo — the document slot label is independent of which
 *  department logo is displayed. */
function LogoSlotEditor({
  label, descriptor, onChange, onPickLogo, resolveLogoThumb, resolveDepartmentLogoThumb, resolveDepartmentSecondLogoThumb, departmentLogoNote,
}: {
  label: string;
  descriptor: LogoDescriptor | undefined;
  onChange: (next: LogoDescriptor | undefined) => void;
  onPickLogo?: () => Promise<string | null>;
  resolveLogoThumb?: (uploadedFileId: string) => string | undefined;
  resolveDepartmentLogoThumb?: () => string | undefined;
  resolveDepartmentSecondLogoThumb?: () => string | undefined;
  departmentLogoNote?: string;
}) {
  const current = descriptor ?? defaultLogoDescriptor();
  const deptSlot = current.departmentLogoSlot ?? 'primary';
  // For department-sourced logos, pick the thumbnail resolver based on which
  // department logo the user selected (primary or secondary).
  const thumb = current.source === 'uploaded'
    ? (current.uploadedFileId ? resolveLogoThumb?.(current.uploadedFileId) : undefined)
    : (deptSlot === 'secondary'
      ? (resolveDepartmentSecondLogoThumb ?? resolveDepartmentLogoThumb)?.()
      : resolveDepartmentLogoThumb?.());
  const testId = label.toLowerCase().includes('secondary') ? 'secondary' : 'primary';

  return (
    <fieldset className="m-0 rounded-md border border-gray-200 p-2" data-testid={`logo-slot-${testId}`}>
      <legend className="px-1 text-[11px] text-gray-500">{label}</legend>
      <label className="mb-1.5 flex items-center gap-1.5 text-xs text-gray-700">
        <input
          type="checkbox"
          aria-label={`Show ${label}`}
          checked={current.show}
          onChange={(e) => onChange({ ...current, show: e.target.checked })}
        />
        Show this logo
      </label>
      <div className={current.show ? '' : 'pointer-events-none opacity-40'}>
        <div className="flex gap-3 text-xs">
          <label className="flex items-center gap-1">
            <input
              type="radio"
              aria-label={`${label} from department`}
              name={`logo-source-${testId}`}
              checked={current.source === 'department'}
              onChange={() => onChange({ ...current, source: 'department' })}
            />
            Department logo
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              aria-label={`${label} uploaded`}
              name={`logo-source-${testId}`}
              checked={current.source === 'uploaded'}
              onChange={() => onChange({ ...current, source: 'uploaded' })}
            />
            Uploaded image
          </label>
        </div>
        {current.source === 'uploaded' && (
          <div className="mt-2 flex items-center gap-2">
            {thumb
              ? <img src={thumb} alt="Uploaded logo preview" className="h-8 w-8 rounded-sm border border-gray-200 object-contain" />
              : <span className="flex h-8 w-8 items-center justify-center rounded-sm border border-dashed border-gray-300 text-gray-300"><ImageIcon className="h-4 w-4" /></span>}
            <button
              type="button"
              aria-label="Upload logo"
              onClick={async () => {
                const id = await onPickLogo?.();
                if (id) onChange({ ...current, source: 'uploaded', uploadedFileId: id });
              }}
              className="rounded-sm border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
            >
              {current.uploadedFileId ? 'Change…' : 'Upload…'}
            </button>
            {current.uploadedFileId && (
              <button
                type="button"
                aria-label="Remove uploaded logo"
                onClick={() => onChange({ source: 'department', show: current.show, departmentLogoSlot: deptSlot })}
                className="text-xs text-red-600 underline"
              >
                Remove
              </button>
            )}
          </div>
        )}
        {current.source === 'department' && (
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {thumb
                ? <img src={thumb} alt="Department logo preview" className="h-8 w-8 rounded-sm border border-gray-200 object-contain" />
                : <span className="flex h-8 w-8 items-center justify-center rounded-sm border border-dashed border-gray-300 text-gray-300"><ImageIcon className="h-4 w-4" /></span>}
              {departmentLogoNote && (
                <span className="max-w-32 text-[10px] leading-tight text-gray-500">{departmentLogoNote}</span>
              )}
            </div>
            <div className="flex gap-3 text-xs">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  aria-label={`${label} department primary`}
                  name={`logo-dept-slot-${testId}`}
                  checked={deptSlot === 'primary'}
                  onChange={() => onChange({ ...current, departmentLogoSlot: 'primary' })}
                />
                Primary
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  aria-label={`${label} department secondary`}
                  name={`logo-dept-slot-${testId}`}
                  checked={deptSlot === 'secondary'}
                  onChange={() => onChange({ ...current, departmentLogoSlot: 'secondary' })}
                />
                Secondary
              </label>
            </div>
          </div>
        )}
      </div>
    </fieldset>
  );
}

export function DocumentCanvasBuilder({
  layout, onChange, sampleData, renderRichTextEditor, onPickLogo, resolveLogoThumb, resolveDepartmentLogoThumb, resolveDepartmentSecondLogoThumb, departmentLogoNote,
  config, onConfigChange,
  selectedId: controlledSelectedId, onSelectedIdChange, activationDriven = false,
  propertiesPortalTarget,
}: DocumentCanvasBuilderProps) {
  const [surface, setSurface] = useState<BuilderSurface>('body');
  // Selection is controlled when the host passes `selectedId` (single-canvas activation mode);
  // otherwise it is self-managed. `setSelectedId` routes through both so all existing call sites work.
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const selectionControlled = controlledSelectedId !== undefined;
  const selectedId = selectionControlled ? controlledSelectedId : internalSelectedId;
  const setSelectedId = useCallback((id: string | null) => {
    if (!selectionControlled) setInternalSelectedId(id);
    onSelectedIdChange?.(id);
  }, [selectionControlled, onSelectedIdChange]);
  const [sampleOpen, setSampleOpen] = useState<Record<string, boolean>>({});
  const [propTab, setPropTab] = useState<'content' | 'style'>('content');
  // Reset to the Content tab whenever the selected band changes — including host-driven
  // selection from the left activation list, which bypasses the internal `selectBand`.
  useEffect(() => { setPropTab('content'); }, [selectedId]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Absolute insert position (index into the active surface's bands) chosen via an inline "+"
  // affordance between bands. `null` → the palette appends to the end (toolbar / empty-state add).
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  // Phase 5 help system: illustrated panel, guided tour, and per-band contextual popover.
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState<HelpSectionId | undefined>(undefined);
  const [tourActive, setTourActive] = useState(false);
  const [helpBandId, setHelpBandId] = useState<string | null>(null);
  const [tourSeen, setTourSeen] = useState<boolean>(() => readTourSeen());
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const openHelpAt = useCallback((section?: HelpSectionId) => {
    setHelpSection(section);
    setHelpOpen(true);
  }, []);

  const startTour = useCallback(() => {
    setHelpBandId(null);
    setTourActive(true);
  }, []);

  const dismissTourPrompt = useCallback(() => {
    markTourSeen();
    setTourSeen(true);
  }, []);

  const finishTour = useCallback(() => {
    setTourActive(false);
    markTourSeen();
    setTourSeen(true);
  }, []);

  // Esc closes an open per-band help popover. (Panel/tour manage their own Esc handling.)
  useEffect(() => {
    if (!helpBandId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setHelpBandId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [helpBandId]);

  const hasCover = !!layout.coverLetter;
  const activeBands = surface === 'coverPage' ? (layout.coverLetter?.bands ?? []) : layout.body;
  // Single-canvas activation mode hides disabled body bands entirely. The sortable list, indices,
  // total, and tour anchors must reflect only the bands that actually render — otherwise
  // SortableContext registers phantom items (no DOM node) and Move up/down use wrong positions.
  const visibleBands = useMemo(
    () => (activationDriven && surface === 'body' ? activeBands.filter((b) => b.enabled !== false) : activeBands),
    [activeBands, activationDriven, surface],
  );
  const bandIds = useMemo(() => visibleBands.map((b) => b.id), [visibleBands]);
  // Tour anchors: spotlight the first dynamic band (step 7) and the first band's controls (8/9).
  const firstBandId = visibleBands[0]?.id;
  const firstDynamicBandId = useMemo(
    () => visibleBands.find((b) => isDynamicBand(b.kind))?.id,
    [visibleBands],
  );
  const selectableBands = useMemo(() => getSelectableBands(layout, surface), [layout, surface]);
  const selectedEntry = selectableBands.find((entry) => entry.band.id === selectedId) ?? null;
  const selected = selectedEntry?.band ?? null;
  const isLogoBand = selected?.kind === 'letterhead' || selected?.kind === 'coverPage';
  // Section↔Canvas parity: the shared section editor for the selected band (merged options).
  const mergedSectionId = selected ? sectionIdForBand(selected) : null;
  const showMergedEditor = !!(mergedSectionId && config && onConfigChange);

  const closeProperties = useCallback(() => setSelectedId(null), [setSelectedId]);
  const selectBand = useCallback((id: string) => { setSelectedId(id); setPropTab('content'); }, [setSelectedId]);

  const handleAdd = useCallback((kind: BandKind, index?: number) => {
    const band = createBand(kind);
    const next = applySurfaceOp(layout, surface, (l) => addBand(l, band, index, surface));
    onChange(next);
    setInsertIndex(null);
    // addBand is a no-op for an already-present singleton; only select when actually inserted.
    const list = surface === 'coverPage' ? (next.coverLetter?.bands ?? []) : next.body;
    if (list.some((b) => b.id === band.id)) selectBand(band.id);
  }, [layout, surface, onChange, selectBand]);

  // Open the palette positioned to insert before the visible band at `visibleIndex`
  // (or at the end when visibleIndex >= count), mapping to the absolute surface index so
  // hidden/disabled bands are accounted for.
  const openPaletteAt = useCallback((visibleIndex: number) => {
    const abs = visibleIndex >= visibleBands.length
      ? activeBands.length
      : activeBands.findIndex((b) => b.id === visibleBands[visibleIndex].id);
    setInsertIndex(abs < 0 ? null : abs);
    setPaletteOpen(true);
  }, [visibleBands, activeBands]);

  const handleDragEnd = useCallback((e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    // Map the drop target to its absolute index within the surface's full band array (which is
    // what moveBand reorders), so the move stays correct even when activation mode hides bands
    // from the sortable list. `over` is always a rendered (visible) band.
    const toIndex = activeBands.findIndex((b) => b.id === String(over.id));
    if (toIndex === -1) return;
    onChange(applySurfaceOp(layout, surface, (l) => moveBand(l, String(active.id), toIndex)));
  }, [activeBands, layout, surface, onChange]);

  const addCoverPage = useCallback(() => {
    onChange({ ...layout, coverLetter: { bands: [] } });
    setSurface('coverPage');
    setSelectedId(null);
    setInsertIndex(null);
    setPaletteOpen(false);
  }, [layout, onChange, setSelectedId]);

  const [confirmRemoveCover, setConfirmRemoveCover] = useState(false);

  const removeCoverPage = useCallback(() => {
    setConfirmRemoveCover(true);
  }, []);

  const doRemoveCoverPage = useCallback(() => {
    onChange({ ...layout, coverLetter: null });
    setSurface('body');
    setSelectedId(null);
    setInsertIndex(null);
    setPaletteOpen(false);
    setConfirmRemoveCover(false);
  }, [layout, onChange, setSelectedId]);

  // Switching surfaces invalidates any pending insert position (it was relative to the
  // previous surface's band list), so close the palette and clear the index.
  const switchSurface = useCallback((next: BuilderSurface) => {
    setSurface(next);
    setSelectedId(null);
    setInsertIndex(null);
    setPaletteOpen(false);
  }, [setSelectedId]);

  return (
    <div className="relative flex min-h-[460px] flex-col" data-testid="document-canvas-builder">
      {/* Keyframe for the fixed properties panel slide-in animation. */}
      <style>{`@keyframes dcb-slide-in-right{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>
      {/* Canvas header: surface tabs + cover-page actions + palette opener */}
      <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2">
        <div className="flex items-center gap-1 rounded-md bg-gray-100 p-0.5" role="tablist" aria-label="Builder surface" data-tour={TOUR_TARGET.surfaceTabs}>
          <button
            type="button"
            role="tab"
            aria-selected={surface === 'body'}
            data-testid="surface-tab-body"
            onClick={() => switchSurface('body')}
            className={`flex items-center gap-1.5 rounded-sm px-3 py-1 text-sm font-medium ${surface === 'body' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FileText className="h-3.5 w-3.5" /> Body
          </button>
          {hasCover && (
            <button
              type="button"
              role="tab"
              aria-selected={surface === 'coverPage'}
              data-testid="surface-tab-cover"
              onClick={() => switchSurface('coverPage')}
              className={`flex items-center gap-1.5 rounded-sm px-3 py-1 text-sm font-medium ${surface === 'coverPage' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Layers className="h-3.5 w-3.5" /> Cover Page
            </button>
          )}
        </div>

        {!hasCover && (
          <button
            type="button"
            data-testid="add-cover-page"
            onClick={addCoverPage}
            className="flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add Cover Page
          </button>
        )}
        {hasCover && surface === 'coverPage' && (
          <button
            type="button"
            data-testid="remove-cover-page"
            onClick={removeCoverPage}
            className="flex items-center gap-1 rounded-md border border-red-200 px-2.5 py-1 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove Cover Page
          </button>
        )}

        <span className="flex-1" />

        <button
          type="button"
          data-testid="open-builder-help"
          onClick={() => openHelpAt()}
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50"
        >
          <HelpCircle className="h-3.5 w-3.5" /> Help
        </button>
        <button
          type="button"
          data-testid="start-builder-tour"
          onClick={startTour}
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Compass className="h-3.5 w-3.5" /> Tour
        </button>

        <button
          type="button"
          data-testid="open-palette"
          data-tour={TOUR_TARGET.addComponent}
          onClick={() => { setInsertIndex(null); setPaletteOpen(true); }}
          className="flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1 text-sm font-medium text-white hover:bg-orange-700"
        >
          <PanelLeftOpen className="h-3.5 w-3.5" /> Add component
        </button>
      </div>

      {/* First-visit tour prompt (Workstream K3-a): shown until taken or dismissed. */}
      {!tourSeen && (
        <div
          data-testid="tour-first-visit-prompt"
          className="mb-3 flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800"
        >
          <Compass className="h-4 w-4 shrink-0" />
          <span className="flex-1">New to the builder? Take a quick tour.</span>
          <button
            type="button"
            data-testid="tour-prompt-start"
            onClick={() => { dismissTourPrompt(); startTour(); }}
            className="rounded-md bg-orange-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-orange-700"
          >
            Start tour
          </button>
          <button
            type="button"
            aria-label="Dismiss tour prompt"
            data-testid="tour-prompt-dismiss"
            onClick={dismissTourPrompt}
            className="rounded-sm p-1 text-orange-500 hover:bg-orange-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Main area: canvas bands + inline properties sidebar */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Full-width paged canvas */}
        <div className="min-w-0 flex-1 flex justify-center bg-gray-100 py-6" data-testid="canvas-page" data-tour={TOUR_TARGET.canvas}>
        <div className="w-full max-w-[816px] rounded-lg border border-gray-300 bg-white p-8 shadow-xs">
          {selectableBands.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center text-gray-400" data-testid="canvas-empty-state">
              <Layers className="h-8 w-8" />
              <p className="text-sm">
                {surface === 'coverPage'
                  ? 'This cover page is empty. Add components to build it.'
                  : 'Your document is empty. Add components to start building.'}
              </p>
              <button
                type="button"
                onClick={() => { setInsertIndex(null); setPaletteOpen(true); }}
                className="mt-1 flex items-center gap-1 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
              >
                <Plus className="h-3.5 w-3.5" /> Add component
              </button>
            </div>
          ) : (
            <>
              {surface === 'body' && layout.header && (!activationDriven || layout.header.enabled !== false) && (
                <FixedBandCard
                  band={layout.header}
                  slot="header"
                  selected={layout.header.id === selectedId}
                  sampleData={sampleData}
                  showSample={!!sampleOpen[layout.header.id]}
                  onSelect={() => selectBand(layout.header!.id)}
                  onDelete={() => { onChange(removeSelectableBand(layout, layout.header!.id)); if (selectedId === layout.header!.id) setSelectedId(null); }}
                  onToggleSample={() => setSampleOpen((s) => ({ ...s, [layout.header!.id]: !s[layout.header!.id] }))}
                />
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={bandIds} strategy={verticalListSortingStrategy}>
                  {visibleBands.map((band, index) => (
                    <React.Fragment key={band.id}>
                      <InsertHere index={index} onInsert={openPaletteAt} />
                      <BandCard
                      band={band}
                      index={index}
                      total={visibleBands.length}
                      selected={band.id === selectedId}
                      sampleData={sampleData}
                      showSample={!!sampleOpen[band.id]}
                      onSelect={() => selectBand(band.id)}
                      onDuplicate={() => onChange(applySurfaceOp(layout, surface, (l) => duplicateBand(l, band.id)))}
                      onDelete={() => { onChange(removeSelectableBand(layout, band.id)); if (selectedId === band.id) setSelectedId(null); }}
                      onMove={(dir) => {
                        // Move relative to the next visible neighbour, then map it back to that
                        // band's absolute index so hidden (disabled) bands are skipped over.
                        const neighbour = visibleBands[index + dir];
                        if (!neighbour) return;
                        const toIndex = activeBands.findIndex((b) => b.id === neighbour.id);
                        if (toIndex === -1) return;
                        onChange(applySurfaceOp(layout, surface, (l) => moveBand(l, band.id, toIndex)));
                      }}
                      onToggleSample={() => setSampleOpen((s) => ({ ...s, [band.id]: !s[band.id] }))}
                      onToggleEnabled={() => onChange(updateBand(layout, band.id, { enabled: band.enabled === false }))}
                      helpOpen={helpBandId === band.id}
                      onToggleHelp={() => setHelpBandId((id) => (id === band.id ? null : band.id))}
                      onCloseHelp={() => setHelpBandId(null)}
                      onOpenFullHelp={(section) => { setHelpBandId(null); openHelpAt(section); }}
                      tourDynamic={band.id === firstDynamicBandId}
                      tourControls={band.id === firstBandId}
                      />
                    </React.Fragment>
                  ))}
                  <InsertHere index={visibleBands.length} onInsert={openPaletteAt} trailing />
                </SortableContext>
              </DndContext>
              {surface === 'body' && layout.footer && (!activationDriven || layout.footer.enabled !== false) && (
                <FixedBandCard
                  band={layout.footer}
                  slot="footer"
                  selected={layout.footer.id === selectedId}
                  sampleData={sampleData}
                  showSample={!!sampleOpen[layout.footer.id]}
                  onSelect={() => selectBand(layout.footer!.id)}
                  onDelete={() => { onChange(removeSelectableBand(layout, layout.footer!.id)); if (selectedId === layout.footer!.id) setSelectedId(null); }}
                  onToggleSample={() => setSampleOpen((s) => ({ ...s, [layout.footer!.id]: !s[layout.footer!.id] }))}
                />
              )}
            </>
          )}
        </div>
        </div>

        {/* Properties panel — fixed right overlay that slides over the preview pane.
            Using position:fixed keeps the canvas full-width regardless of the flex layout
            and avoids the overflow/stacking-context issues that broke earlier approaches:
            absolute positioning was clipped by overflow:hidden, and an inline flex sibling
            at w-72 compressed both the canvas and the adjacent live-preview panel. */}
        {selected && (() => {
          // Portal mode is requested when the host passes the `propertiesPortalTarget` prop at all
          // (even as null while its container mounts). In that mode the panel fills the host so it
          // can be shown as a tab beside the preview; if the host isn't mounted yet, render nothing
          // (rather than briefly flashing the fixed overlay). When the prop is omitted entirely, keep
          // the default fixed right-side slide-over overlay.
          const usePortalMode = propertiesPortalTarget !== undefined;
          if (usePortalMode && !propertiesPortalTarget) return null;
          const panel = (
          <aside
            role="complementary"
            aria-label="Band properties"
            data-testid="slideover-right"
            className={usePortalMode
              ? 'flex h-full w-full flex-col bg-white overflow-hidden'
              : 'fixed right-0 top-0 bottom-0 z-50 w-80 flex flex-col bg-white border-l border-gray-200 shadow-2xl overflow-hidden'}
            style={usePortalMode ? undefined : { animation: 'dcb-slide-in-right 0.2s ease-out' }}
          >
            <header className="shrink-0 flex items-center gap-2 border-b border-gray-200 px-4 py-3">
              <h2 className="flex-1 truncate text-sm font-semibold text-gray-800">
                {selectedEntry ? `${slotLabel(selectedEntry.slot)} · ` : ''}{paletteItemFor(selected.kind)?.label ?? selected.kind}
              </h2>
              <button
                type="button"
                aria-label="Close panel"
                onClick={closeProperties}
                className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-orange-400"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <div data-testid="properties-panel" className="flex-1 overflow-y-auto">
            {/* Enabled toggle */}
            <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2">
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={selected.enabled !== false}
                  onChange={(e) => onChange(updateBand(layout, selected.id, { enabled: e.target.checked }))}
                />
                Enabled
              </label>
            </div>
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              {(['content', 'style'] as const).map((t) => (
                <button key={t} type="button" onClick={() => setPropTab(t)}
                  className={`flex-1 border-none py-1.5 text-xs capitalize ${propTab === t ? 'border-b-2 border-orange-500 bg-white font-semibold' : 'bg-gray-50 font-normal'}`}>
                  {t}
                </button>
              ))}
            </div>
            {/* Content tab */}
            {propTab === 'content' && (
              <div className="flex flex-col gap-3 p-3">
                {selected.kind === 'richText' && (
                  <div data-testid="richtext-editor-slot">
                    {renderRichTextEditor
                      ? renderRichTextEditor({
                          html: selected.elements[0]?.html ?? '',
                          onChange: (html) => onChange(updateBand(layout, selected.id, {
                            elements: [{ id: selected.elements[0]?.id ?? `${selected.id}-el`, type: 'text', html }],
                          })),
                        })
                      : (
                        <textarea
                          className="min-h-[120px] w-full text-xs"
                          value={selected.elements[0]?.html ?? ''}
                          onChange={(e) => onChange(updateBand(layout, selected.id, {
                            elements: [{ id: selected.elements[0]?.id ?? `${selected.id}-el`, type: 'text', html: e.target.value }],
                          }))}
                        />
                      )}
                  </div>
                )}
                {isLogoBand && (
                  <div className="flex flex-col gap-2" data-testid="logo-slots">
                    <LogoSlotEditor
                      label="Primary logo"
                      descriptor={selected.primaryLogo}
                      onChange={(next) => onChange(updateBand(layout, selected.id, { primaryLogo: next }))}
                      onPickLogo={onPickLogo}
                      resolveLogoThumb={resolveLogoThumb}
                      resolveDepartmentLogoThumb={resolveDepartmentLogoThumb}
                      resolveDepartmentSecondLogoThumb={resolveDepartmentSecondLogoThumb}
                      departmentLogoNote={departmentLogoNote}
                    />
                    <LogoSlotEditor
                      label="Secondary logo"
                      descriptor={selected.secondaryLogo}
                      onChange={(next) => onChange(updateBand(layout, selected.id, { secondaryLogo: next }))}
                      onPickLogo={onPickLogo}
                      resolveLogoThumb={resolveLogoThumb}
                      resolveDepartmentLogoThumb={resolveDepartmentLogoThumb}
                      resolveDepartmentSecondLogoThumb={resolveDepartmentSecondLogoThumb}
                      departmentLogoNote={departmentLogoNote}
                    />
                  </div>
                )}
                {!showMergedEditor && selected.kind === 'violationsList' && (
                  <label className="block text-xs">
                    Display format
                    <select
                      className="mt-1 block w-full"
                      value={String((selected.config?.displayFormat as string) ?? 'checklist')}
                      onChange={(e) => onChange(updateBand(layout, selected.id, { config: { ...selected.config, displayFormat: e.target.value } }))}
                    >
                      <option value="checklist">Checklist</option>
                      <option value="narrative">Narrative</option>
                      <option value="table">Table</option>
                      <option value="grouped">Grouped</option>
                    </select>
                  </label>
                )}
                {showMergedEditor && (
                  <div
                    data-testid="merged-section-editor"
                    className={isLogoBand ? 'border-t border-gray-200 pt-3' : ''}
                  >
                    <DocumentSectionEditor
                      sectionId={mergedSectionId!}
                      selectedSection={sectionConfigFor(config!, mergedSectionId!)}
                      config={config!}
                      onChange={onConfigChange!}
                      hideLogoControls={isLogoBand}
                    />
                  </div>
                )}
              </div>
            )}
            {/* Style tab */}
            {propTab === 'style' && (
              <div className="flex flex-col gap-2.5 p-3">
                {TEXT_STYLEABLE_KINDS.has(selected.kind) && (
                  <fieldset className="m-0 rounded-md border border-gray-200 p-2">
                    <legend className="px-1 text-[11px] text-gray-500">Text styling</legend>
                    <ElementStyleControls
                      label="Text"
                      testIdPrefix="band-text-style"
                      value={selected.elements.find((el) => el.type === 'text')?.style}
                      onChange={(style) => onChange(updateBand(layout, selected.id, {
                        elements: setPrimaryTextStyle(selected.elements, style),
                      }))}
                    />
                  </fieldset>
                )}
                <label className="flex flex-col gap-1 text-xs">
                  Background colour
                  <input
                    type="color"
                    value={selected.style?.background ?? '#ffffff'}
                    onChange={(e) => onChange(updateBand(layout, selected.id, { style: { ...selected.style, background: e.target.value } }))}
                    className="h-7 w-full cursor-pointer rounded-sm border border-gray-300 p-px"
                  />
                </label>
                <fieldset className="m-0 rounded-md border border-gray-200 p-2">
                  <legend className="px-1 text-[11px] text-gray-500">Border</legend>
                  <div className="grid grid-cols-2 gap-1.5">
                    <label className="flex flex-col gap-0.5 text-[11px]">
                      Width (pt)
                      <input type="number" min={0} max={20}
                        value={selected.style?.border?.width ?? 0}
                        onChange={(e) => onChange(updateBand(layout, selected.id, {
                          style: { ...selected.style, border: { ...(selected.style?.border ?? { style: 'solid' as const, color: '#000000' }), width: Number(e.target.value) } },
                        }))}
                        className="rounded-sm border border-gray-300 px-1 py-0.5 text-xs"
                      />
                    </label>
                    <label className="flex flex-col gap-0.5 text-[11px]">
                      Style
                      <select
                        value={selected.style?.border?.style ?? 'solid'}
                        onChange={(e) => onChange(updateBand(layout, selected.id, {
                          style: { ...selected.style, border: { ...(selected.style?.border ?? { width: 1, color: '#000000' }), style: e.target.value as BorderStyle['style'] } },
                        }))}
                        className="rounded-sm border border-gray-300 px-1 py-0.5 text-xs"
                      >
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                        <option value="double">Double</option>
                      </select>
                    </label>
                    <label className="flex flex-col gap-0.5 text-[11px]">
                      Colour
                      <input type="color"
                        value={selected.style?.border?.color ?? '#000000'}
                        onChange={(e) => onChange(updateBand(layout, selected.id, {
                          style: { ...selected.style, border: { ...(selected.style?.border ?? { width: 1, style: 'solid' as const }), color: e.target.value } },
                        }))}
                        className="h-6 cursor-pointer rounded-sm border border-gray-300 p-px"
                      />
                    </label>
                    <label className="flex flex-col gap-0.5 text-[11px]">
                      Radius (pt)
                      <input type="number" min={0} max={40}
                        value={selected.style?.border?.radius ?? 0}
                        onChange={(e) => onChange(updateBand(layout, selected.id, {
                          style: { ...selected.style, border: { ...(selected.style?.border ?? { width: 1, style: 'solid' as const, color: '#000000' }), radius: Number(e.target.value) } },
                        }))}
                        className="rounded-sm border border-gray-300 px-1 py-0.5 text-xs"
                      />
                    </label>
                  </div>
                </fieldset>
                <fieldset className="m-0 rounded-md border border-gray-200 p-2">
                  <legend className="px-1 text-[11px] text-gray-500">Padding (pt)</legend>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(['top', 'bottom', 'left', 'right'] as const).map((side) => (
                      <label key={side} className="flex flex-col gap-0.5 text-[11px]">
                        {side.charAt(0).toUpperCase() + side.slice(1)}
                        <input type="number" min={0} max={100}
                          value={selected.style?.padding?.[side] ?? 0}
                          onChange={(e) => onChange(updateBand(layout, selected.id, {
                            style: { ...selected.style, padding: { ...(selected.style?.padding ?? {}), [side]: Number(e.target.value) } },
                          }))}
                          className="rounded-sm border border-gray-300 px-1 py-0.5 text-xs"
                        />
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label className="flex flex-col gap-0.5 text-xs">
                  Columns
                  <select
                    value={String(selected.style?.columns ?? 1)}
                    onChange={(e) => onChange(updateBand(layout, selected.id, { style: { ...selected.style, columns: Number(e.target.value) } }))}
                    className="rounded-sm border border-gray-300 px-1.5 py-1 text-xs"
                  >
                    <option value="1">1 column</option>
                    <option value="2">2 columns</option>
                    <option value="3">3 columns</option>
                  </select>
                </label>
                <label className="flex items-start gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={selected.keepTogether === true}
                    onChange={(e) => onChange(updateBand(layout, selected.id, { keepTogether: e.target.checked }))}
                    className="mt-0.5"
                    data-testid="band-keep-together"
                  />
                  <span>
                    Keep together
                    <span className="block text-[11px] text-gray-500">Don&apos;t split this block across pages.</span>
                  </span>
                </label>
              </div>
            )}
            </div>
          </aside>
          );
          return propertiesPortalTarget ? createPortal(panel, propertiesPortalTarget) : panel;
        })()}
      </div>

      {/* Left palette drawer (Workstream I — overlay, not an inline column) */}
      <SlideOverPanel side="left" width={260} open={paletteOpen} onClose={() => { setPaletteOpen(false); setInsertIndex(null); }} title="Add component">
        <div className="p-3" data-testid="component-palette">
          {insertIndex !== null && (
            <div className="mb-2 rounded-sm border border-orange-200 bg-orange-50 px-2 py-1 text-[11px] text-orange-700" data-testid="palette-insert-note">
              Inserting at position {insertIndex + 1}
            </div>
          )}
          {(['structural', 'content', 'dynamic'] as PaletteGroup[]).map((group) => {
            const groupItems = paletteItemsForSurface(surface).filter((i) => i.group === group);
            if (groupItems.length === 0) return null;
            return (
            <div key={group} className="mb-3">
              <div className="mb-1 text-[11px] font-bold uppercase text-gray-500">{GROUP_LABEL[group]}</div>
              {groupItems.map((item) => {
                const singletonPresent = !item.allowMultiple && selectableBands.some(({ band }) => band.kind === item.kind);
                return (
                  <button
                    key={item.kind}
                    type="button"
                    disabled={singletonPresent}
                    title={item.description}
                    onClick={() => handleAdd(item.kind, insertIndex ?? undefined)}
                    data-testid={`palette-${item.kind}`}
                    className={`mb-1 flex w-full items-center gap-1.5 rounded-sm border px-2 py-1.5 text-left text-xs ${singletonPresent ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300' : 'border-gray-200 bg-white text-gray-800 hover:border-orange-400 hover:bg-orange-50'}`}
                  >
                    <Plus className="h-3 w-3 shrink-0" /> {item.label}
                  </button>
                );
              })}
            </div>
            );
          })}
        </div>
      </SlideOverPanel>

      {/* Phase 5 — illustrated help panel (Workstream J) */}
      <BuilderHelpPanel
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onStartTour={() => { setHelpOpen(false); startTour(); }}
        initialSectionId={helpSection}
      />

      {/* Phase 5 — interactive guided tour (Workstream K) */}
      <GuidedTour steps={BUILDER_TOUR_STEPS} active={tourActive} onComplete={finishTour} />

      {/* Cover page removal confirmation */}
      {confirmRemoveCover && (
        <ConfirmActionModal
          title="Remove Cover Page?"
          description="This will permanently remove the cover page and all its content. This cannot be undone."
          confirmButtonLabel="Remove Cover Page"
          variant="danger"
          onConfirm={doRemoveCoverPage}
          onClose={() => setConfirmRemoveCover(false)}
        />
      )}
    </div>
  );
}
